<template>
  <div
    v-if="showBanner"
    class="fixed z-[60] left-3 right-3 bottom-[70px] md:left-4 md:right-auto md:bottom-4 md:w-[380px]"
  >
    <div class="relative bg-sheet rounded-xl shadow-ink border border-border p-4">
      <button
        v-if="store.decided"
        type="button"
        class="absolute top-2 right-2 text-ink2 hover:text-[hsl(var(--foreground))]"
        :aria-label="$t('app.cookieConsent.dismissLabel')"
        @click="store.closeSettings()"
      >
        <X class="w-4 h-4" />
      </button>

      <template v-if="!showCustomizePanel">
        <p class="text-[13px] font-semibold leading-tight pr-4">{{ $t('app.cookieConsent.title') }}</p>
        <p class="text-[11.5px] text-ink2 leading-snug mt-1.5">
          {{ $t('app.cookieConsent.message') }}
        </p>
        <div class="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            class="px-3 py-1.5 rounded-md bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-[12px] font-semibold active:scale-95 transition-transform"
            @click="store.acceptAll()"
          >
            {{ $t('app.cookieConsent.acceptAll') }}
          </button>
          <button
            type="button"
            class="px-3 py-1.5 rounded-md bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] text-[12px] font-semibold active:scale-95 transition-transform"
            @click="store.rejectNonEssential()"
          >
            {{ $t('app.cookieConsent.essentialOnly') }}
          </button>
          <button
            type="button"
            class="px-2 py-1.5 text-ink2 text-[12px] font-semibold underline underline-offset-2"
            @click="expanded = true"
          >
            {{ $t('app.cookieConsent.customize') }}
          </button>
        </div>
      </template>

      <template v-else>
        <p class="text-[13px] font-semibold leading-tight pr-4">{{ $t('app.cookieConsent.customizeTitle') }}</p>

        <ul class="mt-3 space-y-3">
          <li class="flex items-start gap-3">
            <div class="flex-1 min-w-0">
              <p class="text-[12.5px] font-semibold">{{ $t('app.cookieConsent.essential.title') }}</p>
              <p class="text-[11px] text-ink2 leading-snug mt-0.5">
                {{ $t('app.cookieConsent.essential.description') }}
              </p>
            </div>
            <span class="mt-0.5 shrink-0 text-[10px] font-semibold text-ink2">
              {{ $t('app.cookieConsent.alwaysOn') }}
            </span>
          </li>
          <li class="flex items-start gap-3">
            <div class="flex-1 min-w-0">
              <p class="text-[12.5px] font-semibold">{{ $t('app.cookieConsent.analytics.title') }}</p>
              <p class="text-[11px] text-ink2 leading-snug mt-0.5">
                {{ $t('app.cookieConsent.analytics.description') }}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              :aria-checked="analyticsEnabled"
              :aria-label="$t('app.cookieConsent.analytics.title')"
              class="mt-0.5 shrink-0 relative w-9 h-5 rounded-full transition-colors"
              :class="analyticsEnabled ? 'bg-[hsl(var(--primary))]' : 'bg-[hsl(var(--muted))]'"
              @click="analyticsEnabled = !analyticsEnabled"
            >
              <span
                class="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-card shadow transition-transform"
                :class="analyticsEnabled ? 'translate-x-4' : ''"
              />
            </button>
          </li>
        </ul>

        <div class="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            class="px-3 py-1.5 rounded-md bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-[12px] font-semibold active:scale-95 transition-transform"
            @click="save"
          >
            {{ $t('app.cookieConsent.save') }}
          </button>
          <button
            v-if="!store.decided"
            type="button"
            class="px-3 py-1.5 rounded-md text-ink2 text-[12px] font-semibold"
            @click="expanded = false"
          >
            {{ $t('app.cookieConsent.back') }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { X } from "lucide-vue-next";
import { useCookieConsentStore } from "../store/cookieConsent";

const store = useCookieConsentStore();

// Только локальное состояние UI - ничего не сохраняется, пока не нажаты
// "Сохранить"/"Принять все"/"Только необходимые", поэтому закрытие панели
// кнопкой X без сохранения не трогает уже сохранённый выбор.
const expanded = ref(false);
const analyticsEnabled = ref(false);

// Повторное открытие из Профиль > Настройки сразу ведёт на панель настройки,
// предзаполненную сохранённым выбором, а не на приветственный экран.
watch(
  () => store.settingsOpen,
  (open) => {
    if (open) {
      expanded.value = true;
      analyticsEnabled.value = store.analyticsConsent ?? false;
    }
  }
);

const showBanner = computed(() => !store.decided || store.settingsOpen);
const showCustomizePanel = computed(() => expanded.value || store.settingsOpen);

const save = () => store.saveCustom(analyticsEnabled.value);
</script>
